import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { faUserPlus } from "@fortawesome/pro-regular-svg-icons";

import { InputField } from "@/components/InputField/InputField";
import { SelectField, SelectFieldOptions } from "@/components/SelectField/SelectField";
import { Toggle } from "@/components/Toggle/Toggle";
import { EnrichLinkedinConnectionsMode, ListStatus, ListVisibility } from "@/enum/list.enum";
import {
	useGetLinkedinEnrichmentCostPreviewQuery,
	useGetLinkedinEnrichmentSettingsQuery,
	useUpdateLinkedinEnrichmentSettingsMutation,
} from "@/pages/Private/redux/account/account.api";
import { runOrToast } from "@/utils/runOrToast";

import {
	LinkedinEnrichmentSettings,
	UpdateLinkedinEnrichmentSettings,
} from "../schema/linkedin-enrichment";
import { SettingsCard } from "./SettingsCard";
import { SettingsSubhead, SettingsSubBlock } from "./SettingsSection";

/** How much of the network to enrich — maps the mockup's selector onto mode/limit fields. */
enum HowMany {
	ALL = "ALL",
	RECENT_1000 = "RECENT_1000",
	RECENT_2000 = "RECENT_2000",
	MONTHLY = "MONTHLY",
	NEW_ONLY = "NEW_ONLY",
}

const DEFAULT_MONTHLY_LIMIT = 100;

/** Reconstruct the selector value from the persisted settings (mode + monthly/recent limits). */
const fromSettings = (
	settings?: LinkedinEnrichmentSettings
): { howMany: HowMany; monthlyLimit: number } => {
	if (!settings || settings.mode === EnrichLinkedinConnectionsMode.NEW_ONLY) {
		return { howMany: HowMany.NEW_ONLY, monthlyLimit: DEFAULT_MONTHLY_LIMIT };
	}

	if (settings.monthlyLimit) {
		return { howMany: HowMany.MONTHLY, monthlyLimit: settings.monthlyLimit };
	}

	if (settings.recentLimit === 1000) {
		return { howMany: HowMany.RECENT_1000, monthlyLimit: DEFAULT_MONTHLY_LIMIT };
	}

	if (settings.recentLimit === 2000) {
		return { howMany: HowMany.RECENT_2000, monthlyLimit: DEFAULT_MONTHLY_LIMIT };
	}

	return { howMany: HowMany.ALL, monthlyLimit: DEFAULT_MONTHLY_LIMIT };
};

const toSelection = (
	howMany: HowMany,
	monthlyLimit: number
): Pick<UpdateLinkedinEnrichmentSettings, "mode" | "recentLimit" | "monthlyLimit"> => {
	switch (howMany) {
		case HowMany.RECENT_1000:
			return { mode: EnrichLinkedinConnectionsMode.ALL, recentLimit: 1000, monthlyLimit: null };
		case HowMany.RECENT_2000:
			return { mode: EnrichLinkedinConnectionsMode.ALL, recentLimit: 2000, monthlyLimit: null };
		case HowMany.MONTHLY:
			// "Monthly limit" enriches the whole network gradually, capped per month — so it runs
			// in ALL mode (backlog included), with the cap applied per run by the scraper.
			return {
				mode: EnrichLinkedinConnectionsMode.ALL,
				recentLimit: null,
				monthlyLimit,
			};
		case HowMany.NEW_ONLY:
			return {
				mode: EnrichLinkedinConnectionsMode.NEW_ONLY,
				recentLimit: null,
				monthlyLimit: null,
			};
		case HowMany.ALL:
		default:
			return { mode: EnrichLinkedinConnectionsMode.ALL, recentLimit: null, monthlyLimit: null };
	}
};

/**
 * "Connection enrichment" card (PREC-3531): enrich your LinkedIn connections into a private,
 * auto-managed list. Lets the user choose how many to enrich (with a live credit-cost preview)
 * and whether the enriched connections are visible to their team.
 */
export const LinkedinEnrichmentCard: FunctionComponent = () => {
	const { t } = useTranslation();
	const tc = (key: string) => t(`account.connectionEnrichment.${key}`);

	const { data: settings } = useGetLinkedinEnrichmentSettingsQuery();
	const [updateSettings, { isLoading: isSaving }] = useUpdateLinkedinEnrichmentSettingsMutation();

	// Default to "new connections only" — the safe, low-cost option (matches the backend default);
	// hydrated from the persisted settings once they load (and after each save) below.
	const [howMany, setHowMany] = useState<HowMany>(HowMany.NEW_ONLY);
	const [monthlyLimit, setMonthlyLimit] = useState<number>(DEFAULT_MONTHLY_LIMIT);

	// Reflect the saved selection on load / refetch so a reload doesn't reset the dropdown.
	useEffect(() => {
		const restored = fromSettings(settings);

		setHowMany(restored.howMany);
		setMonthlyLimit(restored.monthlyLimit);
	}, [settings]);

	const enabled = !!settings?.enrich;
	const teamVisible = settings?.visibility === ListVisibility.PUBLIC;
	const hasLinkedinAccount = settings?.hasLinkedinAccount ?? false;
	// Enrichment being on does not mean its list is running: the user can pause the list itself
	// (PREC-4601). The toggle stays checked — the setting is unchanged — and the note points at the
	// list page where the existing activation control lives. Nothing here resumes the list.
	const pausedListId =
		enabled && settings?.listStatus === ListStatus.PAUSED ? (settings.listId ?? null) : null;

	const selection = useMemo(() => toSelection(howMany, monthlyLimit), [howMany, monthlyLimit]);

	// For the monthly option, preview the per-month cost (capped at the monthly limit) rather than
	// the whole backlog — that's what the user is billed for in any given month.
	const previewLimit =
		howMany === HowMany.MONTHLY ? monthlyLimit : (selection.recentLimit ?? undefined);

	const { data: preview, isFetching: isPreviewLoading } = useGetLinkedinEnrichmentCostPreviewQuery(
		{ mode: selection.mode, recentLimit: previewLimit },
		{ skip: !hasLinkedinAccount }
	);

	const save = async (overrides: Partial<UpdateLinkedinEnrichmentSettings>) => {
		await runOrToast(
			() =>
				updateSettings({
					enrich: enabled,
					...selection,
					visibility: settings?.visibility,
					...overrides,
				}).unwrap(),
			t("errors.saveFailed")
		);
	};

	const howManyOptions: SelectFieldOptions = [
		{ title: tc("howManyAll"), value: HowMany.ALL },
		{ title: tc("howManyRecent1000"), value: HowMany.RECENT_1000 },
		{ title: tc("howManyRecent2000"), value: HowMany.RECENT_2000 },
		{ title: tc("howManyMonthly"), value: HowMany.MONTHLY },
		{ title: tc("howManyNewOnly"), value: HowMany.NEW_ONLY },
	];

	return (
		<SettingsCard icon={faUserPlus} subtitle={tc("subtitle")} title={tc("title")}>
			<div className="px-5 py-4">
				<div className="flex items-start gap-3">
					<Toggle
						className="mt-0.5"
						disabled={isSaving || !hasLinkedinAccount}
						isChecked={enabled}
						name="enrich-connections"
						testId="enrich-connections-toggle"
						onChange={() => {
							void save({ enrich: !enabled });
						}}
					/>

					<div className="min-w-0 flex-1">
						<span className="text-sm font-semibold text-text">{tc("enrichToggle")}</span>
						<p className="mt-1.5 text-[12.5px] leading-relaxed text-text-meta">
							{hasLinkedinAccount ? tc("enrichDescription") : tc("noLinkedinAccount")}
						</p>
					</div>
				</div>

				{pausedListId !== null && (
					<p
						className="mt-3 rounded-md border border-status-paused bg-status-paused-light px-3 py-2 text-[12.5px] leading-relaxed text-text"
						data-testid="enrichment-paused-note"
					>
						{tc("pausedListNote")}{" "}
						<Link
							className="cursor-pointer font-semibold text-brand-light underline"
							to={`/app/lists/detail/${pausedListId}`}
						>
							{tc("pausedListResume")}
						</Link>
					</p>
				)}

				{hasLinkedinAccount && (
					<SettingsSubBlock className="mt-4">
						<SettingsSubhead>{tc("howManySubhead")}</SettingsSubhead>

						<div className="mt-2 flex flex-col gap-3 modal-m:flex-row modal-m:items-end">
							<div className="max-w-[260px] flex-1">
								<SelectField
									// The settings card clips overflow, so the list renders outside it (PREC-4463).
									portal
									hideEmptyValue
									handleSelect={(value) => {
										setHowMany(value as HowMany);

										if (enabled) {
											void save(toSelection(value as HowMany, monthlyLimit));
										}
									}}
									options={howManyOptions}
									selectedOption={howMany}
								/>
							</div>

							{howMany === HowMany.MONTHLY && (
								<div className="max-w-[160px]">
									<InputField
										label={tc("monthlyLimitLabel")}
										min={1}
										name="monthly-limit"
										type="number"
										value={String(monthlyLimit)}
										handleBlur={() => {
											if (enabled) {
												void save(toSelection(HowMany.MONTHLY, monthlyLimit));
											}
										}}
										handleChange={(e) => setMonthlyLimit(Math.max(1, Number(e.target.value) || 1))}
									/>
								</div>
							)}
						</div>

						<div
							className="mt-3 text-[12.5px] text-text-meta"
							data-testid="enrichment-cost-preview"
						>
							{isPreviewLoading || !preview ? (
								<span>{tc("estimating")}</span>
							) : (
								<span className={preview.sufficient ? "text-text-meta" : "text-status-error"}>
									{tc("costPreview")
										.replace("{{count}}", String(preview.connectionCount))
										.replace("{{credits}}", String(preview.estimatedCredits))
										.replace("{{available}}", String(preview.availableCredits))}
								</span>
							)}
						</div>

						<div className="mt-4 flex items-start gap-3 border-t border-gray-100 pt-4">
							<Toggle
								className="mt-0.5"
								disabled={isSaving}
								isChecked={teamVisible}
								name="team-visible"
								testId="team-visible-toggle"
								onChange={() => {
									void save({
										visibility: teamVisible ? ListVisibility.PRIVATE : ListVisibility.PUBLIC,
									});
								}}
							/>

							<div className="min-w-0 flex-1">
								<span className="text-sm font-semibold text-text">{tc("teamVisibleToggle")}</span>
								<p className="mt-1.5 text-[12.5px] leading-relaxed text-text-meta">
									{tc("teamVisibleDescription")}
								</p>
							</div>
						</div>
					</SettingsSubBlock>
				)}
			</div>
		</SettingsCard>
	);
};
